import {DestroyRef, inject, Injectable} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {debounceTime, distinctUntilChanged} from 'rxjs';

import {OptionsUpdateFacade} from '@selpia/options/data-access';
import {Measure, Perspective, SortingStrategyId} from '@selpia/shared/models';
import {ArrayTools, DateTools, ObjectTools} from '@selpia/shared/utils';
import {AppConfigFacade} from '@selpia/app-config/data-access';
import {DimensionItemsFacade} from '@selpia/dimension-items/data-access';
import {PerspectiveFacade, PerspectiveUpdateFacade} from '@selpia/perspective/data-access';
import {MeasuresFacade, MeasuresHelper} from '@selpia/measures/data-access';

import {NavigationHelper} from '../utils/helpers';

@Injectable()
export class NavigationService {
  readonly #destroyRef = inject(DestroyRef);

  readonly #optionsUpdateFacade = inject(OptionsUpdateFacade);
  readonly #perspectiveUpdateFacade = inject(PerspectiveUpdateFacade);
  readonly #perspectiveFacade = inject(PerspectiveFacade);

  readonly #appConfigFacade = inject(AppConfigFacade);
  readonly #measureFacade = inject(MeasuresFacade);
  readonly #dimensionItemsFacade = inject(DimensionItemsFacade);

  readonly #router: Router = inject(Router);
  readonly #route: ActivatedRoute = inject(ActivatedRoute);

  #isInitialized = false;


  initializeView(
    perspective: Perspective,
    {measure, period, colouringStrategy}: { measure: Measure, period: string, colouringStrategy?: string },
  ): void {
    if (this.#isInitialized) {
      return;
    }

    let urlPerspective: Partial<Perspective> = {};

    try {
      urlPerspective = this.#getPerspectiveFromUrl();
    } catch (e) {
      console.error(`Failed to parse URL query parameters:`, e);
    }

    const initialPerspective: Perspective = {
      filterDimensions: urlPerspective.filterDimensions || perspective.filterDimensions,
      focusDimensionLevel: urlPerspective.focusDimensionLevel || perspective.focusDimensionLevel,
      includeTotal: urlPerspective.includeTotal || perspective.includeTotal,
      date: urlPerspective.date || perspective.date,
    };
    const perspectiveParams = NavigationHelper.getParamsForPerspective(initialPerspective);

    const urlOptionParams = NavigationHelper.getOptionParamsFromUrl();
    const optionParams = {
      m: urlOptionParams.m || measure.code,
      p: urlOptionParams.p || period,
      s: urlOptionParams.s || SortingStrategyId.AlphabeticAsc,
      c: urlOptionParams.c || colouringStrategy
    };

    const queryParams = {...perspectiveParams, ...optionParams};

    this.#router.navigate([], {queryParams, replaceUrl: true})
      .then(() => {
        this.#isInitialized = true;
        this.#watchUrlQuery();
      });
  }

  updatePerspective(partialPerspective: Partial<Perspective>): void {
    const newPerspective = this.#getUpdatedPerspective(partialPerspective);
    if (!newPerspective) {
      return;
    }

    const newParams = NavigationHelper.getParamsForPerspective((newPerspective));
    const queryParams = {...newParams, ...NavigationHelper.getOptionParamsFromUrl()};
    if (queryParams) {
      this.#router.navigate([], {queryParams});
    }
  }

  setFocusPeriod(period: string): void {
    this.#updateOptionsQuery({p: period});
  }

  setFocusMeasure(measure: Measure): void {
    this.#updateOptionsQuery({m: measure.code});
  }

  setSortType(sortType: SortingStrategyId): void {
    if (this.#isInitialized) {
      this.#updateOptionsQuery({s: sortType});
    }
  }

  setColouringStrategy(strategy: string): void {
    this.#updateOptionsQuery({c: strategy});
  }

  #updateOptionsQuery(queryParams: Params) {
    if (document.startViewTransition) {
      document.startViewTransition(() => this.#router.navigate([], {
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
        .then());
      return;
    }

    this.#router.navigate([], {queryParams, queryParamsHandling: 'merge', replaceUrl: true})
      .then();
  }

  #watchUrlQuery(): void {
    this.#route.queryParams
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => !ObjectTools.objChanged(prev, curr)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#setViewFromUrl());
  }

  #setViewFromUrl() {
    const urlPerspective = this.#getUpdatedPerspective(this.#getPerspectiveFromUrl());
    if (urlPerspective) {
      this.#perspectiveUpdateFacade.setPerspective(urlPerspective);
    }

    this.#optionsUpdateFacade.setOptions(this.#getOptionsFromUrl());
  }

  #getUpdatedPerspective(partialPerspective: Partial<Perspective>): Perspective | undefined {
    const currentPerspective = this.#perspectiveFacade.perspective();

    const newPerspective = Object.assign({}, currentPerspective, partialPerspective);
    if (!NavigationHelper.perspectiveChanged(currentPerspective, newPerspective)) {
      return;
    }

    return newPerspective;
  }

  #getPerspectiveFromUrl(): Partial<Perspective> {
    const initialParams = new URLSearchParams(document.location.search);

    const levelName = initialParams.get('q');
    const dimensions = this.#appConfigFacade.dimensions();
    const focusDimensionLevel = levelName ? dimensions?.flatMap(dimension => dimension.levels)
      .find((level => level.name === levelName)) : undefined;
    if (!focusDimensionLevel) {
      throw new Error(`Invalid level name: ${levelName}`);
    }

    const includeTotal = initialParams.get('t') === 'true';

    const date = DateTools.stringToDate(initialParams.get('d'));

    const allDimensionItems = this.#dimensionItemsFacade.items();

    const filterDimensions = ArrayTools.removeFalsy(dimensions?.map(dimension => {
      const itemCode = initialParams.get(dimension.code);
      const items = allDimensionItems?.get(dimension.name);
      if (!itemCode || !items) {
        return undefined;
      }
      return items.find(item => item.code === itemCode);
    }) || []);

    return {
      focusDimensionLevel,
      filterDimensions,
      includeTotal,
      date,
    };
  }

  #getOptionsFromUrl(): Partial<{ sortType: SortingStrategyId, focusMeasure: Measure, focusPeriod: string, colouringStrategy: string }> {
    const params = NavigationHelper.getOptionParamsFromUrl();

    const focusPeriod = params.p;
    const sortType = params.s;
    const colouringStrategy = params.c;

    const measureCode = params.m;
    const measures = this.#measureFacade.measures();
    let focusMeasure: Measure | undefined;
    if (measureCode && measures) {
      focusMeasure = MeasuresHelper.getForCode(measureCode, measures);
    }

    return {focusPeriod, sortType, focusMeasure, colouringStrategy};
  }
}
